/* @flow */
const crypto = require('crypto');
import { BadRequest } from './routes/errors';

// technically returns a buffer, but we're using it as string
export const md5 = (s: string): string => {
    return crypto.createHash('md5').update(s.trim().toLowerCase()).digest('hex')
}

// TODO: unify with client copy of this
// capitalize/prettify string
export const capitalize = (s: string): string => {
    if (typeof s !== 'string') return '';
    let output = s.replace(/([_ ]\w)/g, m => ' ' + m[1].toUpperCase());
    return output.charAt(0).toUpperCase() + output.slice(1);
}

// TODO: unify with client copy of this
// change field name to human-friendly format
export const prettyName  = (field: string): string => {
    if (['income', 'rent', 'home_value', 'age'].indexOf(field) !== -1) {
        field = 'Median_' + field;
    } else if (field === 'house_age') {
        field = 'Avg_house_year';
    } else if (field === 'commute_time') {
        field = `Avg_${field}`
    }
    return capitalize(field);
}

const gcd = (a, b) => {
    if (!b)  {
        return a;
    }
    return gcd(b, a % b);
}

// computes GCD of a list of elements
const gcdList = (list) => {
    let result = list[0];
    for (let i = 1; i < list.length; i++) {
        result = gcd(list[i], result);
        if (result === 1) {
            return 1;
        }
    }
    return result;
}

// helper method for storing maps more efficiently
export const toImageName = (query: Object): string => {
    const keys = Object.keys(query).filter(k => k).sort();
    if (!keys.length) {
        // refuse to store/fetch image without any ranks applied
        throw new BadRequest();
    } else {
        // we compute GCD of the list, then divide all weights by it, normalizing generates maps this way
        // allows us to reuse previous saved maps for other users when weights don't match but ratios do
        const gcd = gcdList(keys.map(k => parseInt(query[k])));
        let name = '';
        keys.forEach(k => {
            name += `${k}_${parseInt(query[k])/gcd}`;
        })
        return name;
    }
}

/* binary search to find value in array
   compareFn should take an argument (a), which represents the current cell in the
   array. This function should return 0 if 'a' is equal to the element being searched
   for, negative number if 'a' is less, and positive number if 'a' is more. Third
   argument is optional and tells the search whether to find the first occurence
   of the argument (default) or last.
 */
export function binarySearch(arr: [], compareFn: Function, first: boolean = true): number {
    let min = 0,
        max = arr.length - 1,
        guess,
        result = -1,
        direction = first ? -1 : +1;
    while (min <= max) {
        guess = Math.floor((min + max) / 2)
    
        if (compareFn(arr[guess]) === 0) {
            result = guess
            min = guess + direction
        } else {
            if (compareFn(arr[guess]) < 0) {
                min = guess + 1
            } else {
                max = guess - 1
            }
        }
    }
  
    return result;
}

/*
    Encodes/decodes sourceInfo key names, tables using this name are typically
    named sourceInfo or availableData in the code. They map a unique stat + year combination,
    and since a single table often contains multiple stats, sometimes requiring a 'where' clause (businesses),
    the real combination is in "table::where::col" format. Where clause can be a string (string must be a 
    JSON.stringify() representation of an object), an object in the same format as knex where clause takes or
    null/undefined. Use null, undefined or '-' to signify lack of where clause.
 */
export const sourceName = {
    encode: (table: string, where: ?string, col: string): string => {
        if (!where) {
            where = '-'
        } else if (typeof where !== 'string') {
            where = JSON.stringify(where)
        }
        return `${table} ${where} ${col}`
    },
    decode: (key: string): [string, ?string, string] => {
        let [table, where, col] = key.split(/ /)
        if (where === '-') {
            where = null
        } else {
            where = JSON.parse(where)
        }
        return [table, where, col]
    }
}

export const range = (from: number, to: number): number[] => {
    const n = to - from + 1
    return Array(n).fill(0).map((e, i) => from + i)
}

// generates table hash for module declarations
export const tables = (baseName: string, fromYear: number | number[], toYear: number, columns: string[]): { [number]: {
    table: string,
    columns?: string[]
}} => {
    const tables = {}
    if (Array.isArray(fromYear)) {
        fromYear.forEach(y => {
            tables[y] = { table: `${baseName}_${y}` }
            if (columns) {
                tables[y].columns = columns
            }
        })
    } else {
        for (let y = fromYear; y <= toYear; y++) {
            tables[y] = { table: `${baseName}_${y}` }
            if (columns) {
                tables[y].columns = columns
            }
        }
    }
    return tables;
}

// wraps around moreDataOnly logic to provide a cleaner way of using sources from other tables
export const sourceFrom = (data) => {
    const t = data.tables
    Object.keys(t).forEach(k => t.columns = data.columns)

    delete data.tables
    delete data.columns

    return {
        moreDataOnly: true,
        moreData: t,
        ...data
    }
}

// find indexes of all occurrences of an element
export const indexOfAll = <T>(arr: T[], val: T): number[] => arr.reduce((acc, el, i) => (el === val ? [...acc, i] : acc), [])
